/**
 * Structural validation for the schedule editor model.
 *
 * Pure and total: any input, including malformed data off the wire, yields a list of issues rather
 * than a throw. The API guard turns the first issue into its BadRequestException; the editor can
 * surface the whole list field by field.
 *
 * REPORTS ONLY — it never normalises. Sorting a date list, defaulting a policy and dropping stray
 * keys are the writer's job (the guard canonicalises on the way in), and folding them in here would
 * make "valid" depend on a repair the caller cannot see.
 *
 * The per-repeat rules mirror what schedule.compile.ts actually reads, so a schedule this
 * function accepts is one the compiler can express. Where the two could drift the compiler wins:
 * a field the compiler ignores for a repeat is rejected here rather than silently discarded.
 */

import { SCHEDULE_DATES_MAX, ScheduleCalendarRule, ScheduleRule, ScheduleKind, ScheduleNonWorkingDayPolicy, ScheduleRepeat } from './schedule.rule';
import { daysInMonth } from './schedule.compile';

export interface ScheduleValidationIssue {
    field: string;
    message: string;
}

const KINDS: readonly ScheduleKind[] = ['perVisit', 'calendar', 'afterService', 'dates'];
const REPEATS: readonly ScheduleRepeat[] = ['daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly'];
const POLICIES: readonly ScheduleNonWorkingDayPolicy[] = ['keep', 'skip', 'next', 'previous', 'nearest'];
const DAILY_PRESETS: readonly string[] = ['everyDay', 'everyNDays', 'weekdays', 'sixDay', 'weekend', 'workingDays'];
const MONTHLY_ON: readonly string[] = ['dayOfMonth', 'nthWeekday'];
const WEEK_SELECTORS: readonly string[] = ['A', 'B', 'isoOdd', 'isoEven'];
const INTERVAL_UNITS: readonly string[] = ['days', 'months', 'years'];
const MONTH_INTERVALS: readonly number[] = [2, 3, 4, 6];
const NTHS: readonly (number | string)[] = [1, 2, 3, 4, 'last'];

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The payload key each kind owns; every other kind's payload is a stray on that kind. */
const KIND_PAYLOADS: Readonly<Record<ScheduleKind, string>> = { perVisit: 'perVisit', calendar: 'calendar', afterService: 'afterService', dates: 'dates' };

/**
 * Every key the calendar editor may carry. The guard drops anything outside this set before storing,
 * so a key that is meaningless for the chosen repeat cannot ride along and reappear later as a
 * pattern nobody authored.
 */
const CALENDAR_KEYS: readonly string[] = ['repeat', 'dailyPreset', 'dailyInterval', 'daysOfWeek', 'weeklyInterval', 'weekSelector', 'monthlyOn', 'daysOfMonth', 'nths', 'weekday', 'monthInterval', 'months', 'ends', 'nonWorkingDayPolicy'];

/** Which calendar fields each repeat is allowed to carry, beyond the shared ends/policy/months. */
const REPEAT_FIELDS: Readonly<Record<ScheduleRepeat, readonly string[]>> = {
    daily: ['dailyPreset', 'dailyInterval'],
    weekly: ['daysOfWeek', 'weeklyInterval'],
    fortnightly: ['daysOfWeek', 'weekSelector'],
    monthly: ['monthlyOn', 'daysOfMonth', 'nths', 'weekday'],
    quarterly: ['monthlyOn', 'daysOfMonth', 'nths', 'weekday', 'monthInterval'],
    yearly: ['monthlyOn', 'daysOfMonth', 'nths', 'weekday'],
};

/** A real calendar date, not merely a well-shaped string: 2026-02-30 matches the pattern and is not a day. */
function isValidLocalDate(value: unknown): value is string {

    if (typeof value !== 'string' || !LOCAL_DATE_PATTERN.test(value)) {
        return false;
    }

    const [year, month, day] = value.split('-').map(Number);

    if (month < 1 || month > 12 || day < 1) {
        return false;
    }

    return day <= daysInMonth(year, month);
}

function isPositiveInteger(value: unknown): value is number {

    return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {

    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Structural issues with a schedule editor model, in field order. An empty array means the schedule
 * is storable as-is (modulo the canonicalisation the writer applies).
 *
 * An absent schedule is valid — it is the working-day default, not a missing value.
 */
export function validateTaskSchedule(schedule: ScheduleRule | null | undefined): ScheduleValidationIssue[] {

    if (schedule === null || schedule === undefined) {
        return [];
    }

    if (!isPlainObject(schedule)) {
        return [{ field: 'kind', message: 'A schedule must be an object' }];
    }

    const kind = schedule.kind;

    if (!KINDS.includes(kind)) {
        return [{ field: 'kind', message: `Unsupported task schedule kind: ${String(kind)}` }];
    }

    const issues = foreignPayloadIssues(schedule, kind);

    switch (kind) {
        case 'perVisit':
            return [...issues, ...validatePerVisit(schedule)];
        case 'afterService':
            return [...issues, ...validateAfterService(schedule)];
        case 'dates':
            return [...issues, ...validateDates(schedule)];
        case 'calendar':
            return [...issues, ...validateCalendar(schedule)];
    }
}

/** A payload belonging to a different kind is rejected rather than ignored: it is the author disagreeing with themselves. */
function foreignPayloadIssues(schedule: ScheduleRule, kind: ScheduleKind): ScheduleValidationIssue[] {

    const own = KIND_PAYLOADS[kind];
    const issues: ScheduleValidationIssue[] = [];

    for (const candidate of KINDS) {
        const key = KIND_PAYLOADS[candidate];

        if (key !== own && (schedule as unknown as Record<string, unknown>)[key] !== undefined) {
            issues.push({ field: key, message: `${key} is not valid on a ${kind} schedule` });
        }
    }

    return issues;
}

function validatePerVisit(schedule: ScheduleRule): ScheduleValidationIssue[] {

    const perVisit = schedule.perVisit;

    if (!isPlainObject(perVisit)) {
        return [{ field: 'perVisit', message: 'A visit interval is required for a per-visit schedule' }];
    }

    if (!isPositiveInteger(perVisit.nth)) {
        return [{ field: 'perVisit.nth', message: 'Task visit interval must be a positive integer' }];
    }

    return [];
}

function validateAfterService(schedule: ScheduleRule): ScheduleValidationIssue[] {

    const afterService = schedule.afterService;

    if (!isPlainObject(afterService)) {
        return [{ field: 'afterService', message: 'An interval is required for an after-service schedule' }];
    }

    const issues: ScheduleValidationIssue[] = [];

    if (!isPositiveInteger(afterService.interval)) {
        issues.push({ field: 'afterService.interval', message: 'Service interval must be a positive integer' });
    }

    if (!INTERVAL_UNITS.includes(afterService.unit)) {
        issues.push({ field: 'afterService.unit', message: `Unsupported service interval unit: ${String(afterService.unit)}` });
    }

    if (afterService.firstDue !== undefined && !isValidLocalDate(afterService.firstDue)) {
        issues.push({ field: 'afterService.firstDue', message: 'First due must be a YYYY-MM-DD date' });
    }

    issues.push(...validateLeeway(afterService.earlyLeeway, 'afterService.earlyLeeway', 'Early'));
    issues.push(...validateLeeway(afterService.lateLeeway, 'afterService.lateLeeway', 'Late'));

    return issues;
}

/** Zero is allowed and reads as "no leeway", so clearing the stepper never makes a schedule unsaveable. */
function validateLeeway(leeway: unknown, field: string, label: string): ScheduleValidationIssue[] {

    if (leeway === undefined) return [];
    if (!isPlainObject(leeway)) return [{ field, message: `${label} leeway must be a value and unit` }];

    const issues: ScheduleValidationIssue[] = [];
    if (!(leeway.value === 0 || isPositiveInteger(leeway.value))) {
        issues.push({ field: `${field}.value`, message: `${label} leeway must be a whole number of zero or more` });
    }
    if (!INTERVAL_UNITS.includes(leeway.unit as string)) {
        issues.push({ field: `${field}.unit`, message: `Unsupported ${label.toLowerCase()} leeway unit: ${String(leeway.unit)}` });
    }

    return issues;
}


function validateDates(schedule: ScheduleRule): ScheduleValidationIssue[] {

    const dates = schedule.dates;

    if (!isPlainObject(dates) || !Array.isArray(dates.dates)) {
        return [{ field: 'dates.dates', message: 'A date list is required for a dates schedule' }];
    }

    const list = dates.dates as unknown[];
    const issues: ScheduleValidationIssue[] = [];

    if (list.length === 0) {
        issues.push({ field: 'dates.dates', message: 'At least one date is required' });
    }

    if (list.length > SCHEDULE_DATES_MAX) {
        issues.push({ field: 'dates.dates', message: `A date list may contain at most ${SCHEDULE_DATES_MAX} dates` });
    }

    const invalid = list.filter(date => !isValidLocalDate(date));

    if (invalid.length > 0) {
        issues.push({ field: 'dates.dates', message: 'Every date must be a valid YYYY-MM-DD calendar date' });
    }

    if (invalid.length === 0) {
        const values = list as string[];

        if (new Set(values).size !== values.length) {
            issues.push({ field: 'dates.dates', message: 'Dates must be unique' });
        }

        const sorted = values.every((date, index) => index === 0 || values[index - 1] < date);

        if (!sorted && new Set(values).size === values.length) {
            issues.push({ field: 'dates.dates', message: 'Dates must be sorted ascending' });
        }
    }

    if (dates.nonWorkingDayPolicy !== undefined && !POLICIES.includes(dates.nonWorkingDayPolicy)) {
        issues.push({ field: 'dates.nonWorkingDayPolicy', message: `Unsupported non-working day policy: ${String(dates.nonWorkingDayPolicy)}` });
    }

    return issues;
}

function validateCalendar(schedule: ScheduleRule): ScheduleValidationIssue[] {

    const calendar = schedule.calendar;

    if (!isPlainObject(calendar)) {
        return [{ field: 'calendar', message: 'Recurrence settings are required for a calendar schedule' }];
    }

    const repeat = calendar.repeat;

    if (!REPEATS.includes(repeat)) {
        return [{ field: 'calendar.repeat', message: `Unsupported recurrence: ${String(repeat)}` }];
    }

    return [
        ...strayKeyIssues(calendar),
        ...sharedCalendarIssues(calendar),
        ...repeatFieldIssues(calendar, repeat),
        ...repeatShapeIssues(calendar, repeat),
    ];
}

/** Keys outside the whitelist entirely — a typo, an older client, or a field from another repeat family. */
function strayKeyIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    return Object.keys(calendar)
        .filter(key => !CALENDAR_KEYS.includes(key) && calendar[key] !== undefined)
        .map(key => ({ field: `calendar.${key}`, message: `Unsupported recurrence field: ${key}` }));
}

/** ends and nonWorkingDayPolicy apply to every repeat; months applies to every repeat but means two different things. */
function sharedCalendarIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const issues: ScheduleValidationIssue[] = [];
    const ends = calendar.ends;

    if (!isPlainObject(ends)) {
        issues.push({ field: 'calendar.ends', message: 'An end condition is required' });
    } else if (ends.type === 'onDate') {
        if (!isValidLocalDate(ends.date)) issues.push({ field: 'calendar.ends.date', message: 'End date must be a YYYY-MM-DD date' });
    } else if (ends.type === 'afterCount') {
        if (!isPositiveInteger(ends.count)) issues.push({ field: 'calendar.ends.count', message: 'Occurrence count must be a positive integer' });
    } else if (ends.type !== 'never') {
        issues.push({ field: 'calendar.ends.type', message: 'Unsupported end condition' });
    }

    if (calendar.nonWorkingDayPolicy !== undefined && !POLICIES.includes(calendar.nonWorkingDayPolicy as ScheduleNonWorkingDayPolicy)) {
        issues.push({ field: 'calendar.nonWorkingDayPolicy', message: `Unsupported non-working day policy: ${String(calendar.nonWorkingDayPolicy)}` });
    }

    return issues;
}

/**
 * Fields that belong to a DIFFERENT repeat than the one chosen.
 *
 * `months` is deliberately absent from the per-repeat lists and never flagged here: the model
 * documents it as valid on every repeat, meaning the occurrence month(s) on yearly and a seasonal
 * restriction on everything else, and compile() honours it on all six via withSeason/byMonthList.
 */
function repeatFieldIssues(calendar: Record<string, unknown>, repeat: ScheduleRepeat): ScheduleValidationIssue[] {

    const allowed = REPEAT_FIELDS[repeat];
    const owned = new Set(Object.values(REPEAT_FIELDS).flat());

    return Object.keys(calendar)
        .filter(key => owned.has(key) && !allowed.includes(key) && calendar[key] !== undefined)
        .map(key => ({ field: `calendar.${key}`, message: `${key} is not valid on a ${repeat} recurrence` }));
}

function repeatShapeIssues(calendar: Record<string, unknown>, repeat: ScheduleRepeat): ScheduleValidationIssue[] {

    const issues = [...monthsIssues(calendar, repeat)];

    switch (repeat) {
        case 'daily':
            return [...issues, ...dailyIssues(calendar)];
        case 'weekly':
            return [...issues, ...weeklyIssues(calendar)];
        case 'fortnightly':
            return [...issues, ...fortnightlyIssues(calendar)];
        case 'monthly':
        case 'quarterly':
        case 'yearly':
            return [...issues, ...monthlyFamilyIssues(calendar, repeat)];
    }
}

/** Yearly REQUIRES months (1–2 entries); every other repeat merely allows them as a seasonal filter. */
function monthsIssues(calendar: Record<string, unknown>, repeat: ScheduleRepeat): ScheduleValidationIssue[] {

    const months = calendar.months;

    if (months === undefined) {
        return repeat === 'yearly' ? [{ field: 'calendar.months', message: 'A yearly recurrence requires one or two months' }] : [];
    }

    if (!Array.isArray(months) || months.length === 0) {
        return [{ field: 'calendar.months', message: 'Months must be a non-empty list' }];
    }

    if (months.some(month => !Number.isInteger(month) || (month as number) < 1 || (month as number) > 12)) {
        return [{ field: 'calendar.months', message: 'Every month must be between 1 and 12' }];
    }

    if (repeat === 'yearly' && months.length > 2) {
        return [{ field: 'calendar.months', message: 'A yearly recurrence allows at most two months' }];
    }

    return [];
}

function dailyIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const preset = calendar.dailyPreset;
    const issues: ScheduleValidationIssue[] = [];

    if (preset === undefined || !DAILY_PRESETS.includes(preset as string)) {
        issues.push({ field: 'calendar.dailyPreset', message: `Unsupported daily preset: ${String(preset)}` });
    }

    // The interval is the everyNDays preset's whole payload, and meaningless on the fixed day sets.
    if (preset === 'everyNDays') {
        if (!Number.isInteger(calendar.dailyInterval) || (calendar.dailyInterval as number) < 2) {
            issues.push({ field: 'calendar.dailyInterval', message: 'Daily interval must be an integer of 2 or more' });
        }
    } else if (calendar.dailyInterval !== undefined) {
        issues.push({ field: 'calendar.dailyInterval', message: 'dailyInterval is only valid with the everyNDays preset' });
    }

    return issues;
}

function daysOfWeekIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const days = calendar.daysOfWeek;

    if (!Array.isArray(days) || days.length === 0) {
        return [{ field: 'calendar.daysOfWeek', message: 'At least one day of the week is required' }];
    }

    if (days.some(day => !Number.isInteger(day) || (day as number) < 1 || (day as number) > 7)) {
        return [{ field: 'calendar.daysOfWeek', message: 'Every day of the week must be between 1 and 7' }];
    }

    return [];
}

function weeklyIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const issues = daysOfWeekIssues(calendar);

    if (calendar.weeklyInterval !== undefined && (!Number.isInteger(calendar.weeklyInterval) || (calendar.weeklyInterval as number) < 1)) {
        issues.push({ field: 'calendar.weeklyInterval', message: 'Weekly interval must be a positive integer' });
    }

    return issues;
}

/** Fortnightly is fixed at two weeks, so it names a phase (weekSelector) and forbids an interval. */
function fortnightlyIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const issues = daysOfWeekIssues(calendar);

    if (calendar.weekSelector === undefined || !WEEK_SELECTORS.includes(calendar.weekSelector as string)) {
        issues.push({ field: 'calendar.weekSelector', message: `Unsupported fortnightly week selector: ${String(calendar.weekSelector)}` });
    }

    return issues;
}

function monthlyFamilyIssues(calendar: Record<string, unknown>, repeat: ScheduleRepeat): ScheduleValidationIssue[] {

    const monthlyOn = calendar.monthlyOn;
    const issues: ScheduleValidationIssue[] = [];

    if (monthlyOn === undefined || !MONTHLY_ON.includes(monthlyOn as string)) {
        return [{ field: 'calendar.monthlyOn', message: `Unsupported monthly selection: ${String(monthlyOn)}` }];
    }

    if (monthlyOn === 'dayOfMonth') {
        issues.push(...daysOfMonthIssues(calendar));

        if (calendar.nths !== undefined) issues.push({ field: 'calendar.nths', message: 'nths is not valid with a dayOfMonth selection' });
        if (calendar.weekday !== undefined) issues.push({ field: 'calendar.weekday', message: 'weekday is not valid with a dayOfMonth selection' });
    } else {
        issues.push(...nthWeekdayIssues(calendar));

        if (calendar.daysOfMonth !== undefined) issues.push({ field: 'calendar.daysOfMonth', message: 'daysOfMonth is not valid with an nthWeekday selection' });
    }

    if (repeat === 'quarterly' && calendar.monthInterval !== undefined && !MONTH_INTERVALS.includes(calendar.monthInterval as number)) {
        issues.push({ field: 'calendar.monthInterval', message: 'Month interval must be 2, 3, 4, or 6' });
    }

    return issues;
}

function daysOfMonthIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const days = calendar.daysOfMonth;

    if (!Array.isArray(days) || days.length === 0) {
        return [{ field: 'calendar.daysOfMonth', message: 'At least one day of the month is required' }];
    }

    const valid = days.every(day => day === 'last' || (Number.isInteger(day) && (day as number) >= 1 && (day as number) <= 31));

    return valid ? [] : [{ field: 'calendar.daysOfMonth', message: "Every day of the month must be 1–31 or 'last'" }];
}

function nthWeekdayIssues(calendar: Record<string, unknown>): ScheduleValidationIssue[] {

    const issues: ScheduleValidationIssue[] = [];
    const nths = calendar.nths;

    if (!Array.isArray(nths) || nths.length === 0) {
        issues.push({ field: 'calendar.nths', message: 'At least one ordinal is required' });
    } else if (nths.some(nth => !NTHS.includes(nth as number | string))) {
        issues.push({ field: 'calendar.nths', message: "Every ordinal must be 1, 2, 3, 4, or 'last'" });
    }

    if (!Number.isInteger(calendar.weekday) || (calendar.weekday as number) < 1 || (calendar.weekday as number) > 7) {
        issues.push({ field: 'calendar.weekday', message: 'A weekday between 1 and 7 is required' });
    }

    return issues;
}
